<template>
  <div class="wheel-pointer-position-field">
    <label v-if="configs.label" class="block mb-2">{{ t(configs.label) }}</label>
    <div class="position-options">
      <button
        v-for="pos in positions"
        :key="pos.value"
        class="position-option"
        :class="{ 'is-selected': selectedPosition === pos.value }"
        :title="t(pos.label)"
        @click="selectPosition(pos.value)"
      >
        <svg viewBox="0 0 40 40" class="position-icon">
          <!-- Circle background -->
          <circle
            cx="20"
            cy="20"
            r="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            opacity="0.3"
          />
          <!-- Pointer arrow -->
          <polygon
            v-if="pos.value === 'top'"
            points="20,2 16,10 24,10"
            fill="currentColor"
          />
          <polygon
            v-else-if="pos.value === 'bottom'"
            points="20,38 16,30 24,30"
            fill="currentColor"
          />
          <polygon
            v-else-if="pos.value === 'left'"
            points="2,20 10,16 10,24"
            fill="currentColor"
          />
          <polygon
            v-else-if="pos.value === 'right'"
            points="38,20 30,16 30,24"
            fill="currentColor"
          />
        </svg>
        <span class="position-label">{{ t(pos.label) }}</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';

export default defineComponent({
  name: 'WheelPointerPositionField',

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const selectedPosition = ref<string>('top');

    const positions = [
      { value: 'top', label: 'templateBuilder.games.wheelPointerPosition.top' },
      { value: 'bottom', label: 'templateBuilder.games.wheelPointerPosition.bottom' },
      { value: 'left', label: 'templateBuilder.games.wheelPointerPosition.left' },
      { value: 'right', label: 'templateBuilder.games.wheelPointerPosition.right' },
    ];

    const parseInitialValue = () => {
      const value = property.value.value;
      if (value && typeof value === 'string' && positions.some(p => p.value === value)) {
        selectedPosition.value = value;
      } else {
        selectedPosition.value = 'top';
      }
    };

    const emitChange = () => {
      property.value.value = selectedPosition.value;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const selectPosition = (position: string) => {
      selectedPosition.value = position;
      emitChange();
    };

    onMounted(() => {
      parseInitialValue();
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      parseInitialValue();
    }, { deep: true });

    return {
      t,
      selectedPosition,
      positions,
      selectPosition,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/_variables.scss';

.wheel-pointer-position-field {
  width: 100%;

  .position-options {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .position-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem;
    border: 2px solid $solitude;
    border-radius: 6px;
    background: $white;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 60px;

    &:hover {
      border-color: $brand-color-primary;
    }

    &.is-selected {
      border-color: $brand-color-primary;
      background: rgba($brand-color-primary, 0.1);

      .position-icon {
        color: $brand-color-primary;
      }
    }

    .position-icon {
      width: 32px;
      height: 32px;
      color: $mid-grey;
      transition: color 0.2s ease;
    }

    .position-label {
      font-size: 0.7rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }
}
</style>
